import React, { useState, useRef, useEffect } from 'react';
import {
  MapContainer as LeafletMapContainer,
  TileLayer,
  ZoomControl,
  ScaleControl,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapContainerProps,
  NormalizedGaushala,
  NormalizedManufacturingUnit,
  MAP_ZOOM_LEVELS,
  INDIA_CENTER,
} from '../../types/gaushalaMap.types';
import GaushalaMarker from './GaushalaMarker';
import ManufacturingMarker from './ManufacturingMarker';
import MapLegend from './MapLegend';

// Fix for Leaflet default icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/**
 * Map Container Component
 *
 * Wraps Leaflet map with:
 * - OpenStreetMap tiles
 * - Gaushala markers
 * - Map controls
 * - Legend
 */

const MapControlsComponent: React.FC<{ onMapReady?: () => void }> = ({
  onMapReady,
}) => {
  const map = useMap();

  useEffect(() => {
    if (onMapReady) {
      onMapReady();
    }
  }, [map, onMapReady]);

  return null;
};

interface ExtendedMapContainerProps extends MapContainerProps {
  manufacturingUnits?: NormalizedManufacturingUnit[];
  onManufacturingClick?: (unit: NormalizedManufacturingUnit) => void;
  selectedManufacturingUnit?: NormalizedManufacturingUnit | null;
}

const MapContainer: React.FC<ExtendedMapContainerProps> = ({
  gaushalas,
  manufacturingUnits = [],
  onMarkerClick,
  onManufacturingClick,
  selectedGaushala,
  selectedManufacturingUnit,
  isLoading = false,
  center = [INDIA_CENTER.latitude, INDIA_CENTER.longitude],
  zoom = 5,
}) => {
  const mapRef = useRef<L.Map>(null);
  const [markerLayer, setMarkerLayer] = useState<L.LayerGroup | null>(null);

  const handleMarkerClick = (gaushala: NormalizedGaushala) => {
    if (onMarkerClick) {
      onMarkerClick(gaushala);
    }
  };

  const handleManufacturingClick = (unit: NormalizedManufacturingUnit) => {
    if (onManufacturingClick) {
      onManufacturingClick(unit);
    }
  };

  // Check if we have any markers to show
  const hasMarkers = gaushalas.length > 0 || manufacturingUnits.length > 0;

  // Set max bounds to restrict map to India region
  useEffect(() => {
    if (mapRef.current) {
      const maxBounds = L.latLngBounds(
        L.latLng(8.0, 68.0),    // Southwest corner of India
        L.latLng(37.5, 97.5)    // Northeast corner of India (includes Jammu & Kashmir)
      );
      mapRef.current.setMaxBounds(maxBounds);
      mapRef.current.fitBounds(maxBounds, { padding: [50, 50] });
      mapRef.current.on('drag', function () {
        mapRef.current?.panInsideBounds(maxBounds, { animate: false });
      });
    }
  }, []);

  return (
    <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden shadow-lg relative isolate">
      {/* Loading overlay - z-20 to stay within map container but not overlap sidebar */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-20 rounded-lg">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-2"></div>
            <p className="text-gray-700 text-sm">Loading map data...</p>
          </div>
        </div>
      )}

      {/* No markers state - z-10 within map container */}
      {!isLoading && !hasMarkers && (
        <div className="absolute inset-0 flex items-center justify-center z-10 rounded-lg">
          <div className="text-center">
            <div className="text-6xl mb-2">📍</div>
            <p className="text-gray-600 text-lg font-semibold">No locations found</p>
            <p className="text-gray-500 text-sm">Try refreshing or check your connection</p>
          </div>
        </div>
      )}

      {/* Leaflet Map — always mounted so tiles render immediately.
          Markers are added/removed reactively; map never unmounts. */}
      <LeafletMapContainer
        ref={mapRef}
        center={center as [number, number]}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
      >
        <MapControlsComponent />
        <ZoomControl position="topright" />
        <ScaleControl position="bottomright" />

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          minZoom={5}
          maxZoom={18}
        />

        {gaushalas.map((gaushala) => (
          <GaushalaMarker
            key={`gaushala-${gaushala.id}`}
            gaushala={gaushala}
            onClick={handleMarkerClick}
            isSelected={selectedGaushala?.id === gaushala.id}
          />
        ))}

        {manufacturingUnits.map((unit) => (
          <ManufacturingMarker
            key={`mfg-${unit.id}`}
            unit={unit}
            onClick={handleManufacturingClick}
            isSelected={selectedManufacturingUnit?.id === unit.id}
          />
        ))}

        {hasMarkers && (
          <MapLegend
            position="topright"
            statistics={{
              total: gaushalas.length,
              manufacturingUnits: manufacturingUnits.length,
            }}
          />
        )}
      </LeafletMapContainer>
    </div>
  );
};

export default MapContainer;
